import { useEffect, useMemo, useState } from 'react';
import { findEntry, listLibrary } from '@/api/library';
import { useAuth } from '@/context/AuthContext';
import { useCatalog } from '@/context/CatalogContext';
import { calcTotals, languageCharges } from '@/utils/pricing';

/**
 * Checkout's line for adding languages to a book the reader already owns, in the
 * same shape useCartItems returns.
 *
 * Not a cart line: it is bought from the book's page, for a copy already in the
 * reader's library. It adds the chosen languages for the REST of that copy's term
 * — the expiry does not move — so each language costs its add-on price times the
 * share of the term left, a fraction the platform reports with the purchase.
 * Coupons never apply. The price here is a preview; the server prices the line
 * from the same purchase before taking any money.
 *
 * `entitlement` is undefined while it is being looked up, null when the reader
 * holds no purchase of this book (or it could not be read).
 */
export function useAddLanguageItems({ bookId, languages = [] }) {
  const { getProduct, status } = useCatalog();
  const { isSignedIn } = useAuth();
  const [library, setLibrary] = useState(undefined);

  useEffect(() => {
    if (!bookId || !isSignedIn) {
      setLibrary(undefined);
      return undefined;
    }
    const controller = new AbortController();
    setLibrary(undefined);
    listLibrary({ signal: controller.signal })
      .then(setLibrary)
      .catch((err) => {
        if (err.name !== 'AbortError') setLibrary(null);
      });
    return () => controller.abort();
  }, [bookId, isSignedIn]);

  const wanted = languages.join(',');
  return useMemo(() => {
    const isCatalogReady = status === 'ready';
    const product = bookId ? getProduct(bookId) : null;
    const entry = Array.isArray(library) ? findEntry(library, product ?? { id: bookId }) : library;
    const base = {
      isCatalogReady,
      entitlement: entry,
      isReady: isCatalogReady && entry !== undefined,
      requiresDelivery: false,
      isMultiCurrency: false,
    };

    const held = entry?.additional_languages ?? [];
    const chosen = wanted
      .split(',')
      .filter((l) => l && !held.includes(l) && (product?.additional_languages ?? []).includes(l));

    if (!product || !entry || chosen.length === 0) {
      return {
        ...base,
        items: [],
        languages: chosen,
        currency: product?.currency ?? null,
        totals: calcTotals({ items: [] }),
      };
    }

    const fraction = Number(entry.addon_term_fraction) || 0;
    const charges = languageCharges(product, chosen).map((c) => ({
      ...c,
      price: Math.round(c.price * fraction * 100) / 100,
    }));
    const price = Math.round(charges.reduce((sum, c) => sum + c.price, 0) * 100) / 100;
    const item = {
      ...product,
      key: `addlang:${bookId}`,
      kind: 'add_languages',
      languages: chosen,
      price,
      basePrice: 0,
      languageCharges: charges,
      qty: 1,
      lineTotal: price,
    };
    return {
      ...base,
      items: [item],
      languages: chosen,
      currency: product.currency,
      totals: calcTotals({ items: [item] }),
    };
  }, [bookId, getProduct, status, library, wanted]);
}
